/**
 * Feed Processing API Client
 *
 * Wraps all feed-processing endpoints (base URL configured via VITE_API_URL).
 */
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES, config } from '@/core/config';
import type { MilkReceivableResponse, VerifyLotRequest, VerifyLotResponse } from '@tms-phoenix/tms-types';
import type {
  ApiResponse,
  ApiOrderListItem,
  ApiOrderDetail,
  EligiblePatient,
  CreateOrderRequest,
  UpdateOrderRequest,
  ApiFeedPrepListResponse,
  ApiScanResult,
  StartFeedPrepBody,
  ApiPrepareResponse,
  ApiContainerInfo,
  FeedCalculatorRequest,
  FeedCalculatorResponse,
  ApiStoredSplitsResponse,
} from '../types/feed-processing.types';

const DEFAULT_HOSPITAL_ID = '00000000-0000-4000-a000-000000000007';
const FEED_PREFIX = API_ROUTES.FEED_MANAGEMENT;
const PATIENT_PREFIX = API_ROUTES.PATIENT;
const api = getApiClient();

// ---- Orders -----------------------------------------------------------------

/** GET /feedManagement/api/v1/feedOrders — List orders with search, filters & pagination. */
export function fetchOrders(params: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string | string[];
  order_type?: string | string[];
  staff_id?: string;
  // Scope the list to one patient/encounter — used by the cancel/reorder panel
  // (WS5/WS6) to fetch a single patient's ACTIVE + PENDING orders.
  patient_id?: string;
  encounter_id?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}) {
  const { status, order_type, search, ...rest } = params;
  const normalizedStatus =
    status === undefined
      ? undefined
      : Array.isArray(status)
        ? status
        : [status];
  const normalizedOrderType =
    order_type === undefined
      ? undefined
      : Array.isArray(order_type)
        ? order_type
        : [order_type];
  // Typed as the post-interceptor shape: the response interceptor already unwraps
  // ApiResponse<T> to T, so resp.data here is the order array itself, not the envelope.
  return api.get<ApiOrderListItem[]>(`${FEED_PREFIX}/feedOrders`, {
    params: {
      ...rest,
      ...(search ? { search } : {}),
      ...(normalizedStatus !== undefined && { status: normalizedStatus }),
      ...(normalizedOrderType !== undefined && { order_type: normalizedOrderType }),
    },
    paramsSerializer: {
      // Fastify's query parser for this endpoint expects repeated keys
      // (`status=ACTIVE`) rather than bracket form (`status[]=ACTIVE`).
      indexes: null,
    },
  });
}

/** GET /feedManagement/api/v1/feedOrders/:order_id — Get a single order by ID. */
export function getOrderById(orderId: string) {
  return api.get<ApiResponse<ApiOrderDetail>>(`${FEED_PREFIX}/feedOrders/${orderId}`);
}

/** POST /feedManagement/api/v1/feedOrders — Create a new feed order. */
export function createOrder(body: CreateOrderRequest) {
  return api.post<ApiOrderDetail>(`${FEED_PREFIX}/feedOrders`, body);
}

/**
 * Update feed order.
 *
 * The feed-management backend does not expose a general PATCH /feedOrders/:order_id
 * endpoint. This method is intentionally unsupported.
 */
export function updateOrder(_orderId: string, _body: UpdateOrderRequest) {
  return Promise.reject(
    new Error('Feed order update is not supported by the current feed-management API.'),
  );
}

/** PATCH /feedManagement/api/v1/feedOrders/:order_id/remove — Deactivate a feed order. */
export function deactivateOrder(orderId: string, reason: string) {
  return api.patch<ApiResponse<ApiOrderDetail>>(`${FEED_PREFIX}/feedOrders/${orderId}/remove`, { reason });
}

/** PATCH /feedManagement/api/v1/feedPrep/:schedule_id/deactivate — Deactivate a feed prep split. */
export function deactivateSplit(
  scheduleId: string,
  body: { reason: string; split_id: string },
) {
  return api.patch(`${FEED_PREFIX}/feedPrep/${scheduleId}/deactivate`, body);
}

// ---- Feeds (Feeds tab) -------------------------------------------------------

/** GET /feedManagement/api/v1/feedPrep/feeds — Paginated list of feed preparations. */
export function fetchFeeds(params: {
  page?: number;
  limit?: number;
  patient_id?: string;
  status?: string | string[];
  from_date?: string;
  to_date?: string;
} = {}) {
  const { page, limit, status, ...filters } = params;
  const normalizedStatus =
    status === undefined
      ? undefined
      : Array.isArray(status) ? status : [status];
  return api.get<ApiResponse<ApiFeedPrepListResponse>>(`${FEED_PREFIX}/feedPrep/feeds`, {
    params: {
      page: page ?? 1,
      limit: limit ?? 20,
      ...filters,
      ...(normalizedStatus !== undefined && { status: normalizedStatus }),
    },
    paramsSerializer: { indexes: null },
  });
}

type ScanFeedOrderParams = {
  value: string;
};

/**
 * GET /feedManagement/api/v1/feedPrep/scan — Scan a patient or product barcode
 * to retrieve all ACTIVE feed orders that match. The backend auto-detects the
 * domain (patient_mrn → receivable → product_sku → product_name).
 */
export function scanFeedOrder(params: ScanFeedOrderParams) {
  if (!params.value) {
    throw new Error('scanFeedOrder: value must be provided');
  }
  return api.get<ApiResponse<ApiScanResult[]>>(`${FEED_PREFIX}/feedPrep/scan`, {
    params: {
      value: params.value,
    },
  });
}

/**
 * POST /feedManagement/api/v1/feedPrep/prepare — Create a feed preparation record.
 */
export function prepareFeed(body: StartFeedPrepBody) {
  return api.post<ApiResponse<ApiPrepareResponse>>(`${FEED_PREFIX}/feedPrep/prepare`, body);
}

/**
 * POST /feedManagement/api/v1/feedCalculator — Calculate feed measures and reserve inventory.
 * Returns measures (quantity per product per feed) and reservations (inventory_id per product).
 * inventory_id from reservations can be used as unit_id in /feedPrep/prepare items.
 */
export function calculateFeed(body: FeedCalculatorRequest) {
  return api.post<ApiResponse<FeedCalculatorResponse>>(`${FEED_PREFIX}/feedCalculator`, body);
}

/** GET /patient/api/v1/patients/eligible-patients — Patients eligible for a scanned product. */
export function fetchEligiblePatients(barcode: string) {
  return api.get<ApiResponse<EligiblePatient[]>>(`${PATIENT_PREFIX}/patients/eligible-patients`, {
    params: { barcode },
  });
}

/** GET /patient/api/v1/patients — Slim patient list for the order patient select. */
export function fetchPatientOptions(search?: string) {
  return api.get<ApiResponse<{ patient_id: string; name: string; mrn: string }[]>>(
    `${PATIENT_PREFIX}/patients`,
    { params: { search, limit: 50 } },
  );
}

/**
 * GET /feedManagement/api/v1/feedPrep/container/:container_identifier
 *
 * Returns full preparation details for a container barcode (e.g. BTL-P202600001).
 * Used in ReDosing to load original volume and patient info from a scanned bottle.
 */
export function fetchContainerInfo(containerIdentifier: string) {
  return api.get<ApiResponse<ApiContainerInfo>>(
    `${FEED_PREFIX}/feedPrep/container/${encodeURIComponent(containerIdentifier)}`,
  );
}

export type VerifyProductsResponse = {
  passed: boolean;
  hard_stops: { product_id: string; product_name: string; allergen_name: string }[];
  warnings: { product_id: string; product_name: string; allergen_name: string }[];
  expired: { product_id: string; lot_id: string }[];
  recalled: { product_id: string; lot_id: string }[];
};

/**
 * POST /feedManagement/api/v1/verifyProducts
 *
 * Verifies one or more products against patient allergens, expiry dates, and
 * recall lists. Returns a `passed` flag plus arrays of hard_stops, warnings,
 * expired and recalled lots.
 */
export function verifyProducts(body: {
  patient_id: string;
  hospital_id: string;
  products: Array<{ product_id: string; lot_id: string }>;
}) {
  return api.post<ApiResponse<VerifyProductsResponse>>(`${FEED_PREFIX}/verifyProducts`, body);
}

/** Params accepted by verifyLot(); hospital_id is optional and falls back to the configured/default hospital. */
export type VerifyLotParams = {
  product_id: string;
  batch_number: string;
  patient_id: string;
  hospital_id?: string;
};

/**
 * POST /feedManagement/api/v1/verifyProducts/verify-lot
 *
 * Pre-receive manual verification of a single product lot. Checks the lot
 * against the patient's active allergens and any active recall (queried
 * from DynamoDB) without requiring the lot to already exist in inventory.
 * Used by ManualVerifyDialog when a clinician manually keys in a Lot Number.
 */
export function verifyLot(params: VerifyLotParams) {
  const body: VerifyLotRequest = {
    product_id: params.product_id,
    batch_number: params.batch_number,
    patient_id: params.patient_id,
    hospital_id: params.hospital_id?.trim() || config.hospitalId?.trim() || DEFAULT_HOSPITAL_ID,
  };
  return api.post<ApiResponse<VerifyLotResponse>>(`${FEED_PREFIX}/verifyProducts/verify-lot`, body);
}

/** GET /feedManagement/api/v1/feedPrep/stored — List stored (remaining) splits for a hospital, optionally filtered by order. */
export function fetchStoredSplits(orderId?: string) {
  return api.get<ApiResponse<ApiStoredSplitsResponse>>(`${FEED_PREFIX}/feedPrep/stored`, {
    params: {
      ...(orderId ? { order_id: orderId } : {}),
    },
  });
}

/**
 * GET /feedManagement/api/v1/feedPrep/:prep_id
 *
 * Returns full preparation details by prep_id, including splits (one per
 * container/bottle). Used to build per-split print labels.
 */
export function getFeedPrepById(prepId: string) {
  return api.get<ApiResponse<ApiContainerInfo>>(
    `${FEED_PREFIX}/feedPrep/${encodeURIComponent(prepId)}`,
  );
}

/**
 * GET /feedManagement/api/v1/feedPrep/:prep_id/non-administered
 *
 * Returns preparation details by prep_id, excluding administered (COMPLETED)
 * splits. Used by the Deactivate Feed dialog so only splits that have not
 * yet been administered are offered for selection/cancellation.
 */
export function getNonAdministeredFeedPrepById(prepId: string) {
  return api.get<ApiResponse<ApiContainerInfo>>(
    `${FEED_PREFIX}/feedPrep/${encodeURIComponent(prepId)}/non-administered`,
  );
}

/**
 * GET /feedManagement/api/v1/feedPrep/scan-item/:barcode
 *
 * Looks up a milk receivable by receipt/barcode for use in the recipe scan flow.
 */
export function scanFeedItem(barcode: string, patientId?: string) {
  return api.get<MilkReceivableResponse>(
    `${FEED_PREFIX}/feedPrep/scan-item/${encodeURIComponent(barcode)}`,
    {
      params: {
        ...(patientId ? { patient_id: patientId } : {}),
      },
    },
  );
}
